import Link from 'next/link';

import { cn } from '@kit/ui/utils';

// Follow the nearest surface foreground; image surfaces can set --logo-foreground.
// This keeps light surfaces dark even inside an app using the dark theme.
function LogoImage({
  className,
  width = 105,
  iconOnly = false,
  slogan = false,
}: {
  className?: string;
  width?: number;
  iconOnly?: boolean;
  slogan?: boolean;
}) {
  if (iconOnly) {
    // Return icon-only version for collapsed sidebar
    return (
      <svg
        width={width}
        className={cn(
          'w-8 text-[color:var(--logo-foreground,var(--foreground,#111111))]',
          className,
        )}
        viewBox="0 0 124.01 135.57"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <g>
          <path
            className="fill-none stroke-current stroke-[10px]"
            d="M81.04,8.28C51.42-2.22,18.78,13.34,8.28,42.96c-4.6,12.98-4.35,27.18.71,39.97,4.64,11.74,13.02,21.59,23.77,28-.64,2.22-1.79,5.7-2.83,8.66l-2.78,7.84,7.86-2.73c.12-.04,11.09-3.85,18.03-6.41,12.82,2.05,26.05-.37,37.38-6.88,11.78-6.77,20.77-17.55,25.32-30.38,10.5-29.62-5.06-62.27-34.68-72.77h0Z"
          />
          <path
            className="fill-current"
            d="M78.68,50.62c3.16,0,12.06.52,12.06,11.52,0,12.15-8.95,14.71-13.43,15.35-2.38.34-9.26.89-16.44.89-6.34,0-12.92-.43-16.82-1.8-8.31-2.92-10.23-8.68-10.23-14.43s3.84-11.51,10.23-11.51h33.84c.19,0,.46,0,.79,0M78.68,42.48h0c-.22,0-.42,0-.59,0h-.19s-33.84,0-33.84,0c-10.3,0-18.37,8.63-18.37,19.64,0,13.72,8.52,19.59,15.67,22.11,4.32,1.52,10.7,2.26,19.52,2.26,7.46,0,14.77-.57,17.58-.97,12.78-1.83,20.41-10.57,20.41-23.4,0-11.94-7.92-19.65-20.19-19.65h0Z"
          />
        </g>
      </svg>
    );
  }

  return (
    <svg
      width={width}
      className={cn(
        'w-[110px] text-[color:var(--logo-foreground,var(--foreground,#111111))]',
        className,
      )}
      viewBox={slogan ? '0 0 285.6 96' : '0 0 285.6 73.25'}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <g>
        <symbol id="icon_light" viewBox="0 0 124.01 135.57">
          <g id="icon_light-2">
            <path
              id="bubble"
              className="fill-none stroke-current stroke-[10px]"
              d="M81.04,8.28C51.42-2.22,18.78,13.34,8.28,42.96c-4.6,12.98-4.35,27.18.71,39.97,4.64,11.74,13.02,21.59,23.77,28-.64,2.22-1.79,5.7-2.83,8.66l-2.78,7.84,7.86-2.73c.12-.04,11.09-3.85,18.03-6.41,12.82,2.05,26.05-.37,37.38-6.88,11.78-6.77,20.77-17.55,25.32-30.38,10.5-29.62-5.06-62.27-34.68-72.77h0Z"
            />
            <g id="visor">
              <g id="visor_outer">
                <path
                  className="fill-current"
                  d="M78.68,50.62c3.16,0,12.06.52,12.06,11.52,0,12.15-8.95,14.71-13.43,15.35-2.38.34-9.26.89-16.44.89-6.34,0-12.92-.43-16.82-1.8-8.31-2.92-10.23-8.68-10.23-14.43s3.84-11.51,10.23-11.51h33.84c.19,0,.46,0,.79,0M78.68,42.48h0c-.22,0-.42,0-.59,0h-.19s-33.84,0-33.84,0c-10.3,0-18.37,8.63-18.37,19.64,0,13.72,8.52,19.59,15.67,22.11,4.32,1.52,10.7,2.26,19.52,2.26,7.46,0,14.77-.57,17.58-.97,12.78-1.83,20.41-10.57,20.41-23.4,0-11.94-7.92-19.65-20.19-19.65h0Z"
                />
              </g>
            </g>
          </g>
        </symbol>

        <symbol id="type_light" viewBox="0 0 196.99 56.66">
          <path className="fill-current" d="M0,41.84V0h7v35.27h18.48v6.57H0Z" />
          <path
            className="fill-current"
            d="M44.69,42.71c-3.18,0-5.97-.69-8.37-2.06-2.4-1.38-4.28-3.28-5.62-5.72-1.35-2.44-2.02-5.25-2.02-8.43,0-3.43.66-6.41,1.98-8.93,1.32-2.53,3.15-4.49,5.49-5.88,2.34-1.39,5.06-2.09,8.14-2.09,3.25,0,6.02.76,8.29,2.28s3.97,3.67,5.07,6.44,1.52,6.03,1.25,9.79h-6.94v-2.56c-.02-3.41-.62-5.9-1.8-7.47-1.18-1.57-3.04-2.35-5.58-2.35-2.87,0-5,.89-6.39,2.66-1.39,1.77-2.09,4.36-2.09,7.77,0,3.18.7,5.64,2.09,7.38s3.43,2.61,6.1,2.61c1.72,0,3.21-.38,4.46-1.15,1.25-.77,2.21-1.87,2.89-3.33l6.92,2.09c-1.2,2.83-3.06,5.03-5.56,6.6-2.51,1.57-5.27,2.35-8.29,2.35ZM33.88,28.1v-5.29h21.59v5.29h-21.59Z"
          />
          <path
            className="fill-current"
            d="M73.59,42.71c-2.27,0-4.18-.43-5.75-1.29-1.57-.86-2.76-2.01-3.56-3.44-.8-1.43-1.21-3.01-1.21-4.74,0-1.51.25-2.87.76-4.08s1.28-2.26,2.32-3.14c1.05-.88,2.4-1.6,4.07-2.16,1.26-.41,2.74-.77,4.43-1.1,1.69-.33,3.53-.63,5.52-.92s4.06-.59,6.23-.92l-2.5,1.42c.02-2.17-.46-3.77-1.45-4.79-.99-1.03-2.65-1.54-5-1.54-1.41,0-2.78.33-4.1.99-1.32.66-2.24,1.79-2.76,3.4l-6.39-2c.77-2.65,2.25-4.78,4.43-6.39,2.18-1.61,5.12-2.41,8.82-2.41,2.79,0,5.24.46,7.37,1.37,2.12.91,3.69,2.4,4.72,4.47.56,1.1.9,2.23,1.02,3.38.12,1.15.17,2.41.17,3.76v19.26h-6.13v-6.8l1.02,1.1c-1.41,2.27-3.06,3.93-4.95,4.98s-4.25,1.58-7.07,1.58ZM74.99,37.13c1.59,0,2.94-.28,4.07-.84,1.12-.56,2.02-1.25,2.69-2.06.67-.81,1.12-1.58,1.35-2.3.37-.89.58-1.91.62-3.06.05-1.15.07-2.09.07-2.8l2.15.64c-2.11.33-3.92.62-5.43.87-1.51.25-2.81.49-3.89.71-1.08.22-2.04.47-2.88.74-.81.29-1.5.63-2.06,1.02-.56.39-.99.83-1.29,1.34-.3.5-.45,1.09-.45,1.77,0,.77.19,1.46.58,2.05.39.59.95,1.06,1.7,1.41s1.67.52,2.77.52Z"
          />
          <path
            className="fill-current"
            d="M110.87,42.71c-2.89,0-5.4-.73-7.55-2.18-2.15-1.45-3.82-3.43-5-5.93-1.18-2.5-1.77-5.32-1.77-8.46s.6-6.01,1.79-8.5c1.19-2.49,2.88-4.46,5.07-5.9s4.77-2.16,7.73-2.16,5.49.72,7.51,2.16c2.02,1.44,3.56,3.41,4.6,5.91,1.05,2.5,1.57,5.33,1.57,8.48s-.52,5.93-1.57,8.44c-1.05,2.51-2.6,4.49-4.68,5.94-2.07,1.45-4.64,2.18-7.7,2.18ZM111.48,56.66c-1.74,0-3.41-.27-5.01-.81-1.6-.54-3.04-1.32-4.31-2.34-1.28-1.02-2.32-2.24-3.14-3.68l6.45-3.2c.6,1.14,1.45,1.99,2.54,2.54,1.09.55,2.26.83,3.5.83,1.45,0,2.75-.26,3.89-.77,1.14-.51,2.03-1.27,2.66-2.27.63-1,.92-2.24.89-3.73v-8.92h.87V10.46h6.13v32.89c0,.79-.03,1.55-.1,2.28-.07.73-.18,1.44-.33,2.14-.46,2.03-1.36,3.7-2.67,5.01-1.32,1.31-2.95,2.28-4.9,2.92s-4.1.96-6.46.96ZM111.95,36.43c1.88,0,3.39-.43,4.55-1.29,1.15-.86,1.99-2.07,2.53-3.62.53-1.55.8-3.34.8-5.38s-.27-3.85-.8-5.39c-.53-1.54-1.36-2.74-2.48-3.6-1.12-.86-2.58-1.29-4.36-1.29-1.88,0-3.43.46-4.65,1.38-1.22.92-2.12,2.16-2.7,3.72-.58,1.56-.87,3.29-.87,5.19s.29,3.66.86,5.22c.57,1.56,1.45,2.79,2.63,3.7,1.18.91,2.68,1.37,4.5,1.37Z"
          />
          <path
            className="fill-current"
            d="M148.35,42.71c-3.18,0-5.97-.69-8.37-2.06-2.4-1.38-4.28-3.28-5.62-5.72-1.35-2.44-2.02-5.25-2.02-8.43,0-3.43.66-6.41,1.98-8.93,1.32-2.53,3.15-4.49,5.49-5.88,2.34-1.39,5.06-2.09,8.14-2.09,3.25,0,6.02.76,8.29,2.28s3.97,3.67,5.07,6.44,1.52,6.03,1.25,9.79h-6.94v-2.56c-.02-3.41-.62-5.9-1.8-7.47-1.18-1.57-3.04-2.35-5.58-2.35-2.87,0-5,.89-6.39,2.66-1.39,1.77-2.09,4.36-2.09,7.77,0,3.18.7,5.64,2.09,7.38s3.43,2.61,6.1,2.61c1.72,0,3.21-.38,4.46-1.15,1.25-.77,2.21-1.87,2.89-3.33l6.92,2.09c-1.2,2.83-3.06,5.03-5.56,6.6-2.51,1.57-5.27,2.35-8.29,2.35ZM137.54,28.1v-5.29h21.59v5.29h-21.59Z"
          />
          <path
            className="fill-current"
            d="M168.14,41.84V10.46h6.25v9.73h.84v21.65h-7.09ZM189.9,41.84v-15.11c0-.99-.07-2.08-.2-3.28-.14-1.2-.45-2.36-.94-3.47-.49-1.11-1.23-2.03-2.21-2.75-.98-.72-2.3-1.07-3.97-1.07-.89,0-1.77.15-2.64.44s-1.66.79-2.37,1.5c-.71.71-1.27,1.68-1.7,2.91-.43,1.23-.64,2.8-.64,4.72l-4.15-1.77c0-2.67.52-5.09,1.55-7.26,1.04-2.17,2.56-3.9,4.58-5.19,2.01-1.29,4.49-1.93,7.44-1.93,2.32,0,4.24.39,5.75,1.16,1.51.77,2.71,1.76,3.6,2.96.89,1.2,1.55,2.48,1.98,3.83.43,1.36.7,2.64.83,3.85s.19,2.19.19,2.95v17.52h-7.09Z"
          />
        </symbol>
      </g>

      <g id="assets">
        <use
          width="124.01"
          height="135.57"
          transform="scale(.54)"
          xlinkHref="#icon_light"
        />
        <use
          width="196.99"
          height="56.66"
          transform="translate(88.61 10.47)"
          xlinkHref="#type_light"
        />
      </g>
      {slogan && (
        <text
          x="88.61"
          y="88"
          textLength="196.99"
          lengthAdjust="spacingAndGlyphs"
          fontFamily="Inter, sans-serif"
          fontSize="11"
          fontWeight="400"
          fill="currentColor"
        >
          Always on, anytime, everywhere
        </text>
      )}
    </svg>
  );
}

export function AppLogo({
  href,
  label,
  className,
  iconOnly = false,
  slogan = false,
}: {
  href?: string | null;
  className?: string;
  label?: string;
  iconOnly?: boolean;
  slogan?: boolean;
}) {
  if (href === null) {
    return (
      <LogoImage className={className} iconOnly={iconOnly} slogan={slogan} />
    );
  }

  return (
    <Link aria-label={label ?? 'Home Page'} href={href ?? '/'} prefetch={true}>
      <LogoImage className={className} iconOnly={iconOnly} slogan={slogan} />
    </Link>
  );
}
